'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface MethodologyItem {
  id: number;
  title: string;
  description: string;
  details: string[];
  icon: string;
}

const MethodologySection = () => {
  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  const methodologies: MethodologyItem[] = [
    {
      id: 1,
      title: 'Data-Driven Decision Making',
      description: 'Every recommendation is backed by comprehensive data analysis and industry benchmarking.',
      details: [
        'Quantitative analysis of current operational metrics',
        'Competitive benchmarking against industry standards',
        'ROI modeling with conservative and optimistic scenarios',
        'Risk assessment and mitigation planning',
        'Success probability scoring based on historical data'
      ],
      icon: 'ChartBarSquareIcon'
    },
    {
      id: 2,
      title: 'Agile Implementation Framework',
      description: 'We use iterative development cycles that allow for continuous feedback and rapid adjustments.',
      details: [
        'Two-week sprint cycles with regular check-ins',
        'Continuous integration and testing protocols',
        'Stakeholder feedback loops at each milestone',
        'Flexible scope management for evolving needs',
        'Transparent progress tracking and reporting'
      ],
      icon: 'ArrowPathIcon'
    },
    {
      id: 3,
      title: 'Human-Centered AI Design',
      description: 'Our AI solutions augment human capabilities rather than replace them, ensuring seamless adoption.',
      details: [
        'User experience research and persona development',
        'Intuitive interface design for non-technical users',
        'Comprehensive training and change management',
        'Feedback mechanisms for continuous improvement',
        'Accessibility and inclusivity considerations'
      ],
      icon: 'UserGroupIcon'
    },
    {
      id: 4,
      title: 'Security-First Architecture',
      description: 'Enterprise-grade security and compliance are built into every solution from the ground up.',
      details: [
        'End-to-end encryption for data in transit and at rest',
        'Role-based access control and audit logging',
        'GDPR, CCPA, and industry-specific compliance',
        'Regular security audits and penetration testing',
        'Disaster recovery and business continuity planning'
      ],
      icon: 'ShieldCheckIcon'
    },
    {
      id: 5,
      title: 'Scalable System Design',
      description: 'Solutions are architected to grow with your business, handling increased volume without degradation.',
      details: [
        'Cloud-native architecture for elastic scaling',
        'Microservices design for modular expansion',
        'Performance optimization and load balancing',
        'Database sharding and caching strategies',
        'Future-proof technology stack selection'
      ],
      icon: 'ArrowTrendingUpIcon'
    },
    {
      id: 6,
      title: 'Continuous Optimization',
      description: 'Post-launch monitoring and refinement ensure your AI systems deliver sustained value over time.',
      details: [
        'Real-time performance monitoring and alerting',
        'A/B testing for continuous improvement',
        'Machine learning model retraining schedules',
        'Quarterly business review and strategy sessions',
        'Proactive optimization recommendations'
      ],
      icon: 'AdjustmentsHorizontalIcon'
    }
  ];

  const toggleExpand = (id: number) => {
    setExpandedItem(expandedItem === id ? null : id);
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {methodologies.map((item) => (
          <div
            key={item.id}
            className="bg-card rounded-xl border border-border shadow-soft overflow-hidden transition-all duration-300 hover:shadow-elevated"
          >
            <button
              onClick={() => toggleExpand(item.id)}
              className="w-full p-6 text-left flex items-start space-x-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name={item.icon as any} size={24} className="text-accent" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                    {item.title}
                  </h3>
                  <Icon
                    name={expandedItem === item.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={20}
                    className="text-muted-foreground flex-shrink-0 ml-2"
                  />
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </button>

            {expandedItem === item.id && (
              <div className="px-6 pb-6 animate-slide-in">
                <div className="pl-16">
                  <h4 className="text-sm font-heading font-semibold text-foreground mb-3">
                    Key Components:
                  </h4>
                  <ul className="space-y-2">
                    {item.details.map((detail, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <Icon
                          name="CheckCircleIcon"
                          size={16}
                          className="text-success flex-shrink-0 mt-0.5"
                        />
                        <span className="text-sm text-foreground/80">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MethodologySection;